import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Slider, Typography,
} from '@mui/material';
import { env } from '../env';

const MAX_WIDTH = 1000;
const BASE_URL = env.apiUrl;

function mimeToExt(mimeType: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
  };
  return map[mimeType] ?? 'jpg';
}

async function getCroppedBlob(imageSrc: string, cropPixels: Area, mimeType: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      let targetW = cropPixels.width;
      let targetH = cropPixels.height;
      if (targetW > MAX_WIDTH) {
        targetH = Math.round((targetH * MAX_WIDTH) / targetW);
        targetW = MAX_WIDTH;
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

      ctx.drawImage(
        image,
        cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
        0, 0, targetW, targetH,
      );

      const quality = mimeType === 'image/jpeg' || mimeType === 'image/webp' ? 0.9 : undefined;
      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        mimeType,
        quality,
      );
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

interface Props {
  onUpload: (url: string) => void;
}

export function ImageUploadButton({ onUpload }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [fileMimeType, setFileMimeType] = useState('image/jpeg');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function uploadDirect(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('image', file, `blog-image.${mimeToExt(file.type)}`);
      const res = await fetch(`${BASE_URL}/api/images/upload`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json() as { url: string };
      onUpload(data.url);
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.type === 'image/svg+xml') {
      uploadDirect(file);
      return;
    }

    setFileMimeType(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  function handleClose() {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setUploadError(null);
  }

  async function handleCropAndUpload() {
    if (!imageSrc || !croppedAreaPixels) return;
    setUploading(true);
    setUploadError(null);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, fileMimeType);
      const formData = new FormData();
      formData.append('image', blob, `blog-image.${mimeToExt(fileMimeType)}`);

      const res = await fetch(`${BASE_URL}/api/images/upload`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json() as { url: string };
      onUpload(data.url);
      handleClose();
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box>
        <Button variant='outlined' size='small' onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? <CircularProgress size={16} /> : 'Upload Image'}
        </Button>
        {uploadError && !imageSrc && (
          <Typography variant='caption' color='error' display='block'>{uploadError}</Typography>
        )}
      </Box>

      <Dialog open={Boolean(imageSrc)} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>Crop Image (16:9)</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Typography color='error' sx={{ mb: 1 }}>{uploadError}</Typography>
          )}
          <Box sx={{ position: 'relative', height: 450, bgcolor: '#111' }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={16 / 9}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Box sx={{ mt: 2, px: 1 }}>
            <Typography variant='body2' gutterBottom>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(_, v) => setZoom(v as number)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
          <Button variant='contained' onClick={handleCropAndUpload} disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : 'Crop & Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
